import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, MessageCircle } from 'lucide-react';
import { AuthContext } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

export default function Messages() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }

    // V1 decoupling: Notes/Messages no longer gates on membership.
    // The membership system remains optional and never blocks core flows.

    const fetchMessages = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/api/messages`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true
        });
        setMessages(response.data);
      } catch (error) {
        console.error('Error fetching messages:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMessages();
  }, [user, navigate]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/messages`,
        {
          to_user_id: 'placeholder',
          post_id: 'placeholder',
          content: newMessage
        },
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true
        }
      );
      setNewMessage('');
      toast.success('Message sent');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <nav className="border-b-4 border-black bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-20">
            <Button
              data-testid="back-button"
              onClick={() => navigate('/dashboard')}
              variant="ghost"
              className="mr-4 border-2 border-black rounded-full"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-2xl md:text-3xl font-black text-slate-900" style={{fontFamily: 'Outfit'}}>Messages</h1>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {messages.length === 0 ? (
          <div className="text-center py-16 border-4 border-dashed border-slate-300 rounded-3xl">
            <div className="w-24 h-24 bg-slate-100 rounded-full mx-auto mb-6 flex items-center justify-center border-2 border-black">
              <MessageCircle className="w-12 h-12 text-slate-400" />
            </div>
            <h3 className="text-2xl font-bold text-slate-900 mb-2" style={{fontFamily: 'Outfit'}}>Your kind is out there.</h3>
            <p className="text-slate-600 mb-6">Connect with members to start chatting</p>
            <Button
              onClick={() => navigate('/home')}
              className="bg-indigo-500 hover:bg-indigo-600 text-white border-2 border-black rounded-full font-bold shadow-brutal"
            >
              Browse Posts
            </Button>
          </div>
        ) : (
          <div className="bg-white border-4 border-black rounded-3xl shadow-brutal-lg overflow-hidden">
            <div className="h-[500px] overflow-y-auto p-6 space-y-4">
              {messages.map(msg => (
                <div
                  key={msg.message_id}
                  data-testid={`message-${msg.message_id}`}
                  className={`flex ${msg.from_user_id === user.user_id ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-xs md:max-w-md px-4 py-3 rounded-2xl border-2 border-black shadow-brutal ${
                      msg.from_user_id === user.user_id
                        ? 'bg-indigo-500 text-white'
                        : 'bg-lime-100 text-slate-900'
                    }`}
                  >
                    <p className="font-medium">{msg.content}</p>
                  </div>
                </div>
              ))}
            </div>

            <form onSubmit={handleSendMessage} className="border-t-4 border-black p-4 bg-slate-50">
              <div className="flex gap-3">
                <Input
                  data-testid="message-input"
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type a message..."
                  className="flex-1 h-14 border-2 border-black rounded-full font-medium"
                />
                <Button
                  data-testid="send-button"
                  type="submit"
                  disabled={!newMessage.trim()}
                  className="bg-indigo-500 hover:bg-indigo-600 text-white border-2 border-black rounded-full px-8 font-bold shadow-brutal"
                >
                  <Send className="w-5 h-5" />
                </Button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}